import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, WifiOff, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { todayKey, parseLocalDate } from '../lib/dateHelpers';
import { plan } from '../lib/hkPriority';
import {
  useHkDay, useHkStaff, updateRoom, sendMessage, ackMessage, nowMinutes, hhmmOf, TO_RECEPTION_KINDS, TO_HK_KINDS,
} from '../lib/hkService';
import HkRoomCard, { HK } from '../components/HkRoomCard';

/**
 * Housekeeping Live — phone / iPad screen for housekeeping (Oct 2026).
 * Replaces the printed Clock housekeeping sheet. Rooms come from the Clock
 * export reception uploads each morning; status, messages and reception's
 * priority overrides update live. Order logic lives in lib/hkPriority.js.
 */
export default function HousekeepingLive({ onNavigate, lockedIn, onLogout }) {
  const { userId } = useAuth();
  const day = todayKey();
  const { rooms, messages, loading, error, lastSync, patchLocal } = useHkDay(day);
  const [now, setNow] = useState(new Date());
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [filter, setFilter] = useState('all');
  const [openRoom, setOpenRoom] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(null);   // room number waiting for a name
  const staff = useHkStaff();
  const wakeLock = useRef(null);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  useEffect(() => {
    const acquire = async () => { try { if ('wakeLock' in navigator && document.visibilityState === 'visible') wakeLock.current = await navigator.wakeLock.request('screen'); } catch (_) { /* unsupported */ } };
    acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => { document.removeEventListener('visibilitychange', acquire); try { wakeLock.current && wakeLock.current.release(); } catch (_) { /* ignore */ } };
  }, []);

  const nowM = nowMinutes(now);
  const hasLodges = rooms.some((r) => r.sectorKey === 'lodges' && r.job !== 'oos' && r.job !== 'vacant');
  const visible = useMemo(() => rooms.filter((r) => filter === 'all' || r.sectorKey === filter), [rooms, filter]);
  const p = useMemo(() => plan(visible, nowM), [visible, nowM]);
  const workable = rooms.filter((r) => !['oos', 'vacant', 'in'].includes(r.job));
  const doneCount = workable.filter((r) => r.status === 'done').length;
  const inbox = messages.filter((m) => m.direction === 'to_hk' && !m.ack_at).slice().reverse();

  const lastCleaner = () => { try { return window.localStorage.getItem('hk.lastCleaner') || ''; } catch (_) { return ''; } };

  const advance = async (r, cleanerName) => {
    // Starting a room needs a name (who is cleaning it) for the times report.
    if ((r.status || 'todo') === 'todo' && !cleanerName) { setPicker(r.room); return; }
    const next = r.status === 'todo' ? 'progress' : r.status === 'progress' ? 'done' : 'todo';
    const now = new Date().toISOString();
    const patch = { status: next, status_at: now, status_by: userId };
    if (next === 'progress') Object.assign(patch, { started_at: now, done_at: null, cleaner: cleanerName });
    if (next === 'done') Object.assign(patch, { done_at: now, override: null, override_at: null, hold_reason: null, guest_waiting: false });
    if (next === 'todo') Object.assign(patch, { done_at: null });
    patchLocal(r.room, patch);
    try { await updateRoom(day, r.room, patch, userId, `status:${next}`); } catch (e) { alert(`Could not save – check Wi-Fi. (${e.message})`); }
  };

  const pickName = (name) => {
    const r = rooms.find((x) => x.room === picker);
    setPicker(null);
    try { window.localStorage.setItem('hk.lastCleaner', name); } catch (_) { /* private mode */ }
    if (r) advance(r, name);
  };

  const longDate = parseLocalDate(day)?.toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long' });
  const sync = lastSync ? lastSync.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' }) : '–';
  const current = openRoom ? rooms.find((r) => r.room === openRoom) : null;
  const tabs = [['all', 'All rooms'], ['old', 'Old corridor'], ['new', 'New corridor'], ...(hasLodges ? [['lodges', 'Lodges']] : [])];

  const sections = [
    { key: 'next', title: 'DO NEXT', color: HK.forest, list: p.next },
    { key: 'then', title: 'THEN, IN THIS ORDER', color: HK.muted, list: p.then },
    { key: 'wait', title: 'WAITING – CAN’T START YET', sub: 'Guest still in, late checkout or on hold. They move up by themselves.', color: HK.amber, list: p.waiting },
    { key: 'lodge', title: 'LODGES – ACROSS THE CAR PARK', sub: 'Do these together in one trip.', color: HK.forest, list: p.lodges },
    { key: 'done', title: 'DONE', color: HK.okText, list: p.done },
  ];

  return (
    <div className="fixed inset-0 z-[80] overflow-y-auto no-print" style={{ background: HK.paper, color: HK.forest }}>
      <div className="sticky top-0 z-10 px-4 pt-3 pb-3 flex flex-col gap-2.5" style={{ background: HK.forest, color: HK.paper }}>
        <div className="flex items-end gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-2xl leading-7" style={{ fontFamily: 'Alice, Georgia, serif' }}>Housekeeping</div>
            <div className="text-[13px]" style={{ color: '#C9C3B2' }}>
              {longDate} · {online ? <>live, updated {sync}</> : <span className="font-bold text-red-300"><WifiOff size={12} className="inline mr-1" />OFFLINE since {sync}</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[22px] font-bold leading-6">{doneCount} / {workable.length}</div>
            <div className="text-xs" style={{ color: '#C9C3B2' }}>rooms clean</div>
          </div>
          {!lockedIn && (
            <button type="button" aria-label="Close" onClick={() => onNavigate && onNavigate('housekeeping')} className="p-2 rounded-sm" style={{ border: '1px solid #4A6355' }}><X size={20} /></button>
          )}
        </div>
        <div className="h-1.5" style={{ background: '#2B4A3B' }}>
          <div className="h-1.5" style={{ background: HK.gold, width: `${workable.length ? Math.round((doneCount / workable.length) * 100) : 0}%` }} />
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} className="flex-1 min-h-[38px] px-3 rounded-sm text-sm font-bold whitespace-nowrap"
              style={filter === k ? { background: HK.paper, color: HK.forest } : { color: HK.paper, border: '1px solid #4A6355' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {!online && <div className="text-center font-bold py-2 text-white" style={{ background: HK.red }}>No connection – showing rooms as of {sync}. Taps may not save.</div>}

      <div className="max-w-5xl mx-auto px-3 pb-24">
        {inbox.map((m) => (
          <div key={m.id} className="mt-3 p-3 flex flex-col gap-2" style={{ background: HK.redBg, border: `2px solid ${HK.red}` }}>
            <div className="text-xs font-bold tracking-wider" style={{ color: HK.red }}>FROM RECEPTION · {hhmmOf(m.created_at)}{m.room ? ` · ROOM ${m.room}` : ''}</div>
            <div className="text-base font-semibold leading-snug">{TO_HK_KINDS[m.kind] || 'Message'}{m.body ? ` – ${m.body}` : ''}</div>
            <button type="button" onClick={() => ackMessage(m.id, userId)} className="self-start min-h-[44px] px-6 rounded-sm text-base font-bold text-white" style={{ background: HK.red }}>Got it</button>
          </div>
        ))}

        {loading && <div className="text-center py-16" style={{ color: HK.muted }}>Loading rooms…</div>}
        {error && <div className="mt-4 p-3 text-sm" style={{ background: HK.redBg, color: HK.red }}>Could not load rooms: {error}</div>}
        {!loading && !error && rooms.length === 0 && (
          <div className="text-center py-16 px-6">
            <Sparkles className="mx-auto mb-3" size={32} style={{ color: HK.gold }} />
            <div className="text-lg font-bold">Today's rooms aren't loaded yet</div>
            <div className="text-sm mt-1" style={{ color: HK.muted }}>Reception uploads the Clock report each morning. This screen fills in by itself.</div>
          </div>
        )}

        {sections.filter((s) => s.list.length).map((s) => (
          <div key={s.key} className="pt-4 flex flex-col gap-2">
            <div className="text-[13px] font-extrabold tracking-[2px]" style={{ color: s.color }}>{s.title}</div>
            {s.sub && <div className="text-[13px] -mt-1" style={{ color: HK.muted }}>{s.sub}</div>}
            <div className="grid gap-2 md:grid-cols-2">
              {s.list.map((r) => (
                <HkRoomCard key={r.room} room={r} kind={s.key === 'lodge' ? 'then' : s.key}
                  onAdvance={() => advance(r)} onOpen={() => setOpenRoom(r.room)} />
              ))}
            </div>
          </div>
        ))}
        {lockedIn && onLogout && (
          <div className="text-center pt-10">
            <button type="button" onClick={() => { if (window.confirm('Sign out of the housekeeping screen?')) onLogout(); }} className="text-sm underline" style={{ color: HK.muted }}>Sign out</button>
          </div>
        )}
      </div>

      {picker && (
        <div className="fixed inset-0 z-[95] flex items-end md:items-center justify-center bg-black/40" onClick={() => setPicker(null)}>
          <div className="w-full md:max-w-md max-h-[90vh] overflow-y-auto rounded-t-lg md:rounded-lg p-4 flex flex-col gap-2" style={{ background: HK.paper }} onClick={(e) => e.stopPropagation()}>
            <div className="text-xl mb-1" style={{ fontFamily: 'Alice, Georgia, serif', color: HK.forest }}>Who is cleaning room {picker}?</div>
            {[lastCleaner(), ...staff.filter((n) => n !== lastCleaner())].filter(Boolean).filter((n, i, a) => a.indexOf(n) === i).map((n, i) => (
              <button key={n} type="button" onClick={() => pickName(n)} className="min-h-[52px] px-4 rounded-sm text-left text-[17px] font-bold"
                style={i === 0 && n === lastCleaner() ? { background: HK.forest, color: HK.paper } : { background: HK.card, color: HK.forest, border: `1px solid ${HK.rule}` }}>
                {n}{i === 0 && n === lastCleaner() ? '  ·  last used' : ''}
              </button>
            ))}
            <button type="button" onClick={() => setPicker(null)} className="min-h-[44px] mt-1 text-sm underline" style={{ color: HK.muted }}>Cancel</button>
          </div>
        </div>
      )}

      {current && (
        <RoomSheet staff={staff} room={current} day={day} userId={userId} busy={busy} setBusy={setBusy} nowM={nowM}
          messages={messages.filter((m) => m.room === current.room)} onClose={() => setOpenRoom(null)}
          onAdvance={() => advance(current)} patchLocal={patchLocal} />
      )}
    </div>
  );
}

function RoomSheet({ staff, room, day, userId, busy, setBusy, onClose, onAdvance, messages, patchLocal, nowM }) {
  const [note, setNote] = useState('');
  const [sent, setSent] = useState('');
  const why = plan([room], nowM);
  const decorated = [...why.next, ...why.waiting, ...why.lodges, ...why.done][0];

  const tell = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      if (kind === 'guest_gone') {
        const patch = { guest_gone: true, guest_gone_at: new Date().toISOString() };
        patchLocal(room.room, patch);
        await updateRoom(day, room.room, patch, userId, 'guest_gone');
      }
      if (kind === 'dnd') {
        patchLocal(room.room, { dnd: true });
        await updateRoom(day, room.room, { dnd: true }, userId, 'dnd');
      }
      if (kind === 'ready') {
        const patch = { status: 'done', status_at: new Date().toISOString(), done_at: new Date().toISOString(), status_by: userId, override: null, override_at: null, hold_reason: null, guest_waiting: false };
        patchLocal(room.room, patch);
        await updateRoom(day, room.room, patch, userId, 'status:done');
      }
      await sendMessage({ day, room: room.room, direction: 'to_reception', kind, body: kind === 'note' ? note.trim() : null, userId });
      setSent(TO_RECEPTION_KINDS[kind]);
      setNote('');
    } catch (e) {
      alert(`Could not send – check Wi-Fi. (${e.message})`);
    } finally { setBusy(false); }
  };

  const toggleInspected = async () => {
    const patch = { inspected: !room.inspected, inspected_at: !room.inspected ? new Date().toISOString() : null };
    patchLocal(room.room, patch);
    try { await updateRoom(day, room.room, patch, userId, 'inspected'); } catch (_) { /* realtime will correct */ }
  };

  const s = room.status || 'todo';
  const quick = ['guest_gone', 'still_in', 'dnd', 'maintenance', 'missing'];
  return (
    <div className="fixed inset-0 z-[90] flex items-end md:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-full md:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-lg md:rounded-lg" style={{ background: HK.paper }} onClick={(e) => e.stopPropagation()}>
        <div className="px-4 pt-3 pb-4 flex flex-col gap-2" style={{ background: HK.forest, color: HK.paper }}>
          <button type="button" onClick={onClose} className="self-start text-[15px] font-semibold min-h-[32px]">← All rooms</button>
          <div className="flex items-baseline gap-3">
            <div className="text-[44px] leading-[44px]" style={{ fontFamily: 'Alice, Georgia, serif' }}>{room.room}</div>
            <div>
              <div className="text-base font-semibold">{room.type}</div>
              <div className="text-[13px]" style={{ color: '#C9C3B2' }}>{room.sector}</div>
            </div>
          </div>
        </div>
        <div className="p-3 flex flex-col gap-3">
          <div className="p-3 flex flex-col gap-1" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
            <div className="text-xs font-bold tracking-wider" style={{ color: HK.muted }}>TODAY</div>
            {(decorated?.why || []).map((w, i) => <div key={i} className="text-[15px] font-semibold" style={{ color: w.tone === 'urgent' ? HK.red : w.tone === 'warn' ? HK.amber : w.tone === 'ok' ? HK.ok : HK.forest }}>{w.text}</div>)}
          </div>
          {room.extras.length > 0 && (
            <div className="p-3 flex flex-col gap-1" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
              <div className="text-xs font-bold tracking-wider" style={{ color: HK.muted }}>PUT IN THE ROOM</div>
              {room.extras.map((x) => <div key={x} className="text-[17px] font-bold py-1">• {x}</div>)}
            </div>
          )}
          {room.job === 'stay' && room.comments?.length > 0 && (
            <div className="p-3 text-sm" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
              <div className="text-xs font-bold tracking-wider mb-1" style={{ color: HK.muted }}>BOOKING NOTE</div>
              {room.comments.join(' · ')}
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onAdvance} className="flex-1 min-h-[56px] rounded-sm text-[17px] font-extrabold"
              style={s === 'todo' ? { background: HK.forest, color: HK.paper } : s === 'progress' ? { background: HK.gold, color: '#1D2A22' } : { border: '1px solid #B9AE95', color: HK.muted }}>
              {s === 'todo' ? 'Start cleaning' : s === 'progress' ? 'Room clean' : 'Undo – not clean'}
            </button>
          </div>
          {room.status !== 'todo' && (
            <label className="flex items-center gap-2.5 text-[15px] font-semibold" style={{ color: HK.forest }}>Cleaned by
              <select value={room.cleaner || ''} aria-label="Cleaned by"
                onChange={(e) => { const v = e.target.value || null; patchLocal(room.room, { cleaner: v }); updateRoom(day, room.room, { cleaner: v }, userId, 'cleaner').catch(() => {}); }}
                className="flex-1 min-h-[44px] px-2 rounded-sm text-base" style={{ border: '1px solid #B9AE95', background: '#fff' }}>
                <option value="">– choose –</option>
                {staff.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2.5 min-h-[44px] text-[15px]" style={{ color: HK.muted }}>
            <input type="checkbox" checked={!!room.inspected} onChange={toggleInspected} className="w-5 h-5" style={{ accentColor: HK.forest }} />
            Supervisor checked (optional)
          </label>
          <div className="p-3 flex flex-col gap-2.5" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
            <div className="text-xs font-bold tracking-wider" style={{ color: HK.muted }}>TELL RECEPTION</div>
            <div className="grid grid-cols-2 gap-2">
              {quick.map((k) => (
                <button key={k} type="button" disabled={busy} onClick={() => tell(k)} className="min-h-[52px] px-2 rounded-sm text-[15px] font-bold"
                  style={{ background: HK.paper, border: '1px solid #B9AE95', color: HK.forest }}>
                  {TO_RECEPTION_KINDS[k].replace(' – check out in Clock', '')}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Write a message" aria-label="Message to reception"
                className="flex-1 min-h-[44px] px-3 rounded-sm text-base" style={{ border: '1px solid #B9AE95', background: '#fff' }} />
              <button type="button" disabled={busy || !note.trim()} onClick={() => tell('note')} className="min-h-[44px] px-4 rounded-sm font-bold"
                style={{ background: HK.forest, color: HK.paper, opacity: note.trim() ? 1 : 0.5 }}>Send</button>
            </div>
            {sent && <div className="text-sm font-semibold px-2.5 py-2" style={{ background: HK.okBg, color: HK.okText }}>Sent to reception: {sent}</div>}
          </div>
          {messages.length > 0 && (
            <div className="flex flex-col gap-1.5 pb-4">
              <div className="text-xs font-bold tracking-wider" style={{ color: HK.muted }}>MESSAGES FOR THIS ROOM</div>
              {messages.slice(0, 8).map((m) => (
                <div key={m.id} className="text-sm leading-5">
                  <b>{m.direction === 'to_hk' ? 'Reception' : 'Housekeeping'} {hhmmOf(m.created_at)}</b> – {(m.direction === 'to_hk' ? TO_HK_KINDS : TO_RECEPTION_KINDS)[m.kind] || 'Message'}{m.body ? `: ${m.body}` : ''}
                  {m.direction === 'to_hk' && m.ack_at ? <span style={{ color: HK.ok }}> · seen</span> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
